import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { DownIcon } from '@trello/nachos/icons/down';
import { Popover, PopoverScreen, usePopover } from '@trello/nachos/popover';

import { AdminRoleDemotionPopoverContent } from './AdminRoleDemotionPopoverContent';
import { ChangeRolePopoverContent } from './ChangeRolePopoverContent';

import styles from './WorkspaceMemberChangeRoleButton.less';

const ChangeRolePopoverScreen = {
  ChangeRole: 0,
  AdminRoleDemotionConfirmation: 1,
} as const;

interface WorkspaceMemberChangeRoleButtonProps {
  membershipType: string;
  memberId: string;
  workspaceId: string;
  isWorkspaceAdmin: boolean;
}

export const WorkspaceMemberChangeRoleButton: FunctionComponent<
  WorkspaceMemberChangeRoleButtonProps
> = ({ membershipType, workspaceId, memberId, isWorkspaceAdmin }) => {
  const { triggerRef, toggle, popoverProps, hide, push } = usePopover();

  const handleClick = useCallback(() => {
    Analytics.sendScreenEvent({
      name: 'changeWorkspacePermissionsInlineDialog',
      containers: formatContainers({ workspaceId }),
    });
    toggle();
  }, [toggle, workspaceId]);

  const showAdminRoleDemotionConfirmation = useCallback(() => {
    push(ChangeRolePopoverScreen.AdminRoleDemotionConfirmation);
  }, [push]);

  return (
    <>
      <Button ref={triggerRef} onClick={handleClick} className={styles.button}>
        {isWorkspaceAdmin ? (
          <FormattedMessage
            id="templates.workspace_member_roles.admin.title"
            defaultMessage="Admin"
            description="Admin"
          />
        ) : (
          <FormattedMessage
            id="templates.workspace_member_roles.normal.title"
            defaultMessage="Normal"
            description="Normal"
          />
        )}
        <span className={styles.overflowIcon}>
          <DownIcon size="small" />
        </span>
      </Button>
      <Popover {...popoverProps}>
        <PopoverScreen
          id={ChangeRolePopoverScreen.ChangeRole}
          title={intl.formatMessage({
            id: 'templates.workspace_member_roles.change-permissions',
            defaultMessage: 'Change permissions',
            description: 'Title for the change permissions popover',
          })}
        >
          <ChangeRolePopoverContent
            hide={hide}
            workspaceId={workspaceId}
            memberId={memberId}
            isWorkspaceAdmin={isWorkspaceAdmin}
            membershipType={membershipType}
            showAdminRoleDemotionConfirmation={
              showAdminRoleDemotionConfirmation
            }
          />
        </PopoverScreen>
        <PopoverScreen
          id={ChangeRolePopoverScreen.AdminRoleDemotionConfirmation}
          title={intl.formatMessage({
            id: 'templates.workspace_member_roles.demote-admin-to-normal-member.title',
            defaultMessage: 'Remove admin privileges?',
            description: 'Title for the demote admin to normal member popover',
          })}
        >
          <AdminRoleDemotionPopoverContent
            hide={hide}
            memberId={memberId}
            workspaceId={workspaceId}
          />
        </PopoverScreen>
      </Popover>
    </>
  );
};
